import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react"
import { getPreferences, updatePreference } from "../api/preferences"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

const PREF_KEY = "layout.sidebar_collapsed"
const SECTION_PREFIX = "layout.sidebar_section."

interface SidebarContextValue {
  isCollapsed: boolean
  toggle: () => void
  setCollapsed: (value: boolean) => void
  isSectionExpanded: (sectionId: string) => boolean
  toggleSection: (sectionId: string) => void
}

const SidebarContext = createContext<SidebarContextValue | undefined>(undefined)

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const SidebarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  useEffect(() => {
    let cancelled = false
    getPreferences().then((prefs) => {
      if (cancelled) return
      const saved = prefs[PREF_KEY]
      setIsCollapsed(saved === undefined ? true : saved === "true")

      const sections: Record<string, boolean> = {}
      for (const key of Object.keys(prefs)) {
        if (key.startsWith(SECTION_PREFIX)) {
          const sectionId = key.slice(SECTION_PREFIX.length)
          sections[sectionId] = prefs[key] === "true"
        }
      }
      setExpandedSections(sections)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const persist = useCallback((collapsed: boolean) => {
    updatePreference(PREF_KEY, String(collapsed))
  }, [])

  const setCollapsed = useCallback(
    (value: boolean) => {
      setIsCollapsed(value)
      persist(value)
    },
    [persist]
  )

  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev
      persist(next)
      return next
    })
  }, [persist])

  const isSectionExpanded = useCallback(
    (sectionId: string) => !!expandedSections[sectionId],
    [expandedSections]
  )

  const toggleSection = useCallback(
    (sectionId: string) => {
      setExpandedSections((prev) => {
        const next = !prev[sectionId]
        updatePreference(`${SECTION_PREFIX}${sectionId}`, String(next))
        return { ...prev, [sectionId]: next }
      })
    },
    []
  )

  // Keyboard shortcut: Cmd+\ (Mac) or Ctrl+\ (non-Mac)
  useEffect(() => {
    const isMac =
      typeof navigator !== "undefined" &&
      /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? navigator.userAgent)

    const handleKeyDown = (e: KeyboardEvent) => {
      const modifier = isMac ? e.metaKey : e.ctrlKey
      if (modifier && e.key === "\\") {
        e.preventDefault()
        toggle()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [toggle])

  const value = useMemo<SidebarContextValue>(
    () => ({ isCollapsed, toggle, setCollapsed, isSectionExpanded, toggleSection }),
    [isCollapsed, toggle, setCollapsed, isSectionExpanded, toggleSection]
  )

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useSidebar = (): SidebarContextValue => {
  const ctx = useContext(SidebarContext)
  if (!ctx) {
    throw new Error("useSidebar must be used within SidebarProvider")
  }
  return ctx
}
